import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { ShopCatalog, ShopProduct } from '@shared/types';
import { Footer, TopBar, useDocumentTitle } from '../../components/Chrome';
import { DeckMock } from './DeckMock';
import { formatPrice, shopApi } from './api';

export function packSummary(p: ShopProduct): string {
  return p.packs
    .map((pack) => {
      const bits = [pack.promptCount && `${pack.promptCount} prompts`, pack.answerCount && `${pack.answerCount} answers`].filter(Boolean);
      return `${pack.name} (${bits.join(', ')})`;
    })
    .join(' + ');
}

export function ShopPage() {
  useDocumentTitle('Card decks');
  const [catalog, setCatalog] = useState<ShopCatalog | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    shopApi.catalog().then(setCatalog).catch((e) => setError(e.message));
  }, []);

  const preorder = catalog?.checkout === 'preorder';
  return (
    <>
      <TopBar />
      <main className="page wide stack shop">
        <header className="shop-hero">
          <span className="chip magenta">Printed decks</span>
          <h1>Take the masti off the screen</h1>
          <p className="muted">
            Playing on your phones is free and always will be. These are real cards in a real box, for game nights, for gifting, and for
            Nani, who keeps misplacing her phone.
          </p>
        </header>
        {preorder && (
          <p className="notice">Our first print run is being made. Pre-order now, pay nothing today, and we'll email you when decks ship.</p>
        )}
        {error && <p className="error">{error}</p>}
        {!catalog && !error && <p className="muted">Loading...</p>}
        <div className="shop-grid">
          {catalog?.products.map((p) => (
            <Link key={p.slug} to={`/shop/${p.slug}`} className="product-tile">
              <DeckMock product={p} />
              <div className="pt-body">
                <div className="pt-title">
                  <h2>{p.name}</h2>
                  <span className="price">{formatPrice(p.priceCents, p.currency)}</span>
                </div>
                <p>{p.description}</p>
                <p className="small muted">
                  {p.kind === 'custom_deck' ? `Your pack, plus ${packSummary(p)}` : packSummary(p)}
                </p>
                <span className="btn btn-primary btn-block">
                  {p.kind === 'custom_deck' ? 'Make it yours' : preorder ? 'Pre-order' : 'Buy'}
                </span>
              </div>
            </Link>
          ))}
        </div>

        <section className="panel stack shop-free">
          <h2>What's free, what's not</h2>
          <ul className="shop-points">
            <li>
              <strong>The game on your phones: free.</strong> Every official pack, custom packs, TV mode and every game mode. No ads, no
              paywall, no account needed.
            </li>
            <li>
              <strong>Printed decks: paid.</strong> Poker-size cards on thick matte cardstock, in a tuck box. Prompts are dark, answers are
              white, and the Hindi lines keep their translations.
            </li>
            <li>
              <strong>Your family deck:</strong> make a custom pack for free, with names, sayings and photos, then order it printed and
              shuffled in with the Classic deck.
            </li>
            <li>
              <strong>Shipping:</strong> US, Canada, UK, India, Australia, New Zealand, UAE, Singapore, Pakistan, Bangladesh and Sri Lanka.
            </li>
          </ul>
          <div className="row">
            <Link className="btn btn-outline" to="/">
              Play free now
            </Link>
            <Link className="btn btn-outline" to="/packs/new">
              Make a custom pack
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
